import {
  createMass,
  massToKilograms,
  massToPounds,
  massToOunces,
  createTemperature,
  temperatureToCelsius,
  temperatureToFahrenheit,
  createLength,
  lengthToFeet,
  lengthToInches,
  lengthToMetres,
  lengthToCentimetres,
  createSpeed,
  speedToFps,
  speedToMs,
  speedToMph,
  speedToKmh,
  speedToKnots,
  createPressure,
  pressureToHectopascal,
  pressureToMmhg,
  pressureToBar,
  pressureToPascal,
  pressureToMillibar,
  LengthUnit,
  lengthToMiles,
  lengthToKilometres,
  massToGrams,
} from 'measure-fns';
import { ConversionOptions } from 'measure-fns/dist/interfaces';

import { CurrentUser } from './hooks/useUserInfo';

export const convertWeight = (
  weight: number,
  user: Partial<CurrentUser>,
  largeUnits = true,
  dynamic = false,
): string => {
  const unit = user?.weightUnit ? user.weightUnit : 'lb';
  const mass = createMass(weight);
  switch (unit) {
    case 'lb':
      if (dynamic) {
        const lbs = massToPounds(createMass(weight), { round: true });
        // eslint-disable-next-line no-magic-numbers
        const oz = massToOunces(createMass(weight), { round: true }) - lbs * 16;
        return `${lbs > 0 ? `${lbs} lb` : ''} ${oz > 0 ? `${oz} oz` : ''}`;
      }
      return largeUnits
        ? `${massToPounds(mass, { round: 1 })} lbs`
        : `${massToOunces(mass, { round: 1 })} oz`;
    case 'kg':
      return largeUnits
        ? `${massToKilograms(mass, { round: 1 })} kg`
        : `${massToGrams(mass, { round: 1 })} g`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertLength = (
  rawLength: number,
  user: Partial<CurrentUser>,
  largeUnits = false,
  dynamic = false,
): string => {
  const unit = user?.lengthUnit ? user.lengthUnit : 'ft';
  const length = createLength(rawLength);
  switch (unit) {
    case 'ft':
      if (dynamic) {
        const ft = lengthToFeet(createLength(rawLength), { round: true });
        // eslint-disable-next-line no-magic-numbers
        const inches = lengthToInches(createLength(rawLength), { round: true }) - ft * 12;
        return `${ft > 0 ? `${ft} ft` : ''} ${inches > 0 ? `${inches} in` : ''}`;
      }
      return largeUnits
        ? `${lengthToFeet(length, { round: true })} ft`
        : `${lengthToInches(length, { round: true })} in`;
    case 'm':
      return largeUnits
        ? `${lengthToMetres(length, { round: true })} m`
        : `${lengthToCentimetres(length, { round: true })} cm`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertDistance = (kilometres: number, user: Partial<CurrentUser>): string => {
  const unit = user?.lengthUnit ? user.lengthUnit : 'ft';
  const distance = createLength(kilometres, LengthUnit.kilometres);
  switch (unit) {
    case 'ft':
      return `${new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 }).format(
        lengthToMiles(distance, { round: 1 }),
      )} miles`;
    case 'm':
      return `${new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 }).format(
        lengthToKilometres(distance, { round: 1 }),
      )} kilometres`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertLengthAsNumber = (
  rawLength: number,
  user: Partial<CurrentUser>,
  largeUnits = false,
): number => {
  const unit = user?.lengthUnit ? user.lengthUnit : 'ft';
  const length = createLength(rawLength);
  switch (unit) {
    case 'ft':
      return largeUnits ? lengthToFeet(length) : lengthToInches(length);
    case 'm':
      return largeUnits ? lengthToMetres(length) : lengthToCentimetres(length);
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertTemperature = (
  rawTemperature: number,
  user: Partial<CurrentUser>,
  options: ConversionOptions = { round: 1 },
): string => {
  const unit = user?.temperatureUnit ? user.temperatureUnit : 'F';
  const temperature = createTemperature(rawTemperature);
  switch (unit) {
    case 'C':
      return `${temperatureToCelsius(temperature, options)} °C`;
    case 'F':
      return `${temperatureToFahrenheit(temperature, options)} °F`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertTemperatureAsNumber = (
  rawTemperature: number,
  user: Partial<CurrentUser>,
  options: ConversionOptions = {},
): number => {
  const unit = user?.temperatureUnit ? user.temperatureUnit : 'F';
  const temperature = createTemperature(rawTemperature);
  switch (unit) {
    case 'C':
      return temperatureToCelsius(temperature, options);
    case 'F':
      return temperatureToFahrenheit(temperature, options);
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertSpeed = (
  rawSpeed: number,
  user: Partial<CurrentUser>,
  options: ConversionOptions = { round: 2 },
): string => {
  const unit = user?.speedUnit ? user.speedUnit : 'mph';
  const speed = createSpeed(rawSpeed);
  switch (unit) {
    case 'm-s':
      return `${speedToMs(speed, options)} m/s`;
    case 'f-s':
      return `${speedToFps(speed, options)} f/s`;
    case 'mph':
      return `${speedToMph(speed, options)} mph`;
    case 'km-h':
      return `${speedToKmh(speed, options)} km/h`;
    case 'kn':
      return `${speedToKnots(speed, options)} kt`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertSpeedAsNumber = (rawSpeed: number, user: Partial<CurrentUser>): number => {
  const unit = user?.speedUnit ? user.speedUnit : 'mph';
  const speed = createSpeed(rawSpeed);
  switch (unit) {
    case 'm-s':
      return speedToMs(speed);
    case 'f-s':
      return speedToFps(speed);
    case 'mph':
      return speedToMph(speed);
    case 'km-h':
      return speedToKmh(speed);
    case 'kn':
      return speedToKnots(speed);
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertPressure = (rawPressure: number, user: Partial<CurrentUser>): string => {
  const unit = user?.pressureUnit ? user.pressureUnit : 'hPa';
  const pressure = createPressure(rawPressure);
  switch (unit.toLowerCase()) {
    case 'hpa':
      return `${pressureToHectopascal(pressure, { round: true })} hPa`;
    case 'mmhg':
      return `${pressureToMmhg(pressure, { round: true })} mmHg`;
    case 'bar':
      return `${pressureToBar(pressure, { round: true })} bar`;
    case 'pa':
      return `${pressureToPascal(pressure, { round: true })} Pa`;
    case 'mbar':
      return `${pressureToMillibar(pressure, { round: true })} mbar`;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertPressureAsNumber = (
  rawPressure: number,
  user: Partial<CurrentUser>,
): number => {
  const unit = user?.pressureUnit ? user.pressureUnit : 'hPa';
  const pressure = createPressure(rawPressure);
  switch (unit.toLowerCase()) {
    case 'hpa':
      return pressureToHectopascal(pressure);
    case 'mmhg':
      return pressureToMmhg(pressure);
    case 'bar':
      return pressureToBar(pressure);
    case 'pa':
      return pressureToPascal(pressure);
    case 'mbar':
      return pressureToMillibar(pressure);
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
};

export const convertCount = (count: number): string => {
  return new Intl.NumberFormat('en-US').format(count);
};
